/**
 * 🕒 Phase 15.0.2 — Per-Campaign Timeline / Activity Log Modal
 *
 * Click any row in the Promo Health "Awaiting drip" list → opens this
 * modal showing the full chronological audit trail for one campaign:
 * scheduler drips, manual admin drips, milestone crossings, freeze
 * events, scheduled end, and any promo clears.
 *
 * Each event is visually distinguished by tone (blue=scheduler,
 * amber=manual, purple=milestone/freeze, rose=warning/cap/clear,
 * emerald=start, gray=informational).
 *
 * Mobile responsive — single column vertical timeline that scales
 * gracefully down to ~320px wide.
 */
import { useEffect, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const TONE_PALETTE = {
  emerald: { dot: "bg-emerald-400 shadow-[0_0_8px_rgba(16,185,129,0.7)]", ring: "border-emerald-400/40",  text: "text-emerald-200" },
  amber:   { dot: "bg-amber-400 shadow-[0_0_8px_rgba(251,191,36,0.7)]",   ring: "border-amber-400/45",   text: "text-amber-200"   },
  blue:    { dot: "bg-sky-400 shadow-[0_0_8px_rgba(56,189,248,0.65)]",     ring: "border-sky-400/35",     text: "text-sky-200"     },
  yellow:  { dot: "bg-yellow-300 shadow-[0_0_8px_rgba(253,224,71,0.75)]", ring: "border-yellow-300/55",  text: "text-yellow-200"  },
  purple:  { dot: "bg-purple-400 shadow-[0_0_8px_rgba(168,85,247,0.65)]", ring: "border-purple-400/40", text: "text-purple-200"  },
  rose:    { dot: "bg-rose-400 shadow-[0_0_10px_rgba(244,63,94,0.75)]",   ring: "border-rose-400/45",    text: "text-rose-200"    },
  gray:    { dot: "bg-gray-400",                                          ring: "border-gray-400/30",    text: "text-gray-300"    },
};

const TYPE_ICON = {
  campaign_start:  "🚀",
  initial_burst:   "✨",
  scheduler_drip:  "💧",
  manual_drip:     "🛠️",
  milestone:       "🎯",
  frozen:          "🧊",
  scheduled_end:   "⏳",
  promo_cleared:   "🗑️",
  cap_reached:     "⛔",
};

const FILTERS = [
  { key: "all",       label: "All",                  match: () => true },
  { key: "scheduler", label: "Scheduler",            match: (e) => e.type === "scheduler_drip" || e.type === "initial_burst" },
  { key: "manual",    label: "Manual",               match: (e) => e.is_manual },
  { key: "milestones",label: "Milestones",           match: (e) => e.type === "milestone" },
  { key: "warnings",  label: "Warnings",             match: (e) => e.is_warning },
];

const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const relTime = (iso) => {
  if (!iso) return "";
  try {
    const diffMs = Date.now() - new Date(iso).getTime();
    const future = diffMs < 0;
    const abs = Math.abs(diffMs);
    const mins = Math.floor(abs / 60000);
    const hrs  = Math.floor(mins / 60);
    const days = Math.floor(hrs / 24);
    const text =
      days >= 1 ? `${days}d` :
      hrs  >= 1 ? `${hrs}h`  :
      mins >= 1 ? `${mins}m` : "now";
    return future ? `in ${text}` : `${text} ago`;
  } catch {
    return "";
  }
};

const SummaryPill = ({ label, value, tone = "amber", testid }) => {
  const t = TONE_PALETTE[tone] || TONE_PALETTE.amber;
  return (
    <div className={`rounded-lg border bg-black/30 px-2.5 py-1.5 ${t.ring}`} data-testid={testid}>
      <p className={`text-[9px] uppercase tracking-wider font-black ${t.text}`}>{label}</p>
      <p className="text-white font-black text-sm leading-tight mt-0.5">{value}</p>
    </div>
  );
};

const TimelineEvent = ({ event }) => {
  const tone = TONE_PALETTE[event.tone] || TONE_PALETTE.gray;
  const icon = TYPE_ICON[event.type] || "•";

  return (
    <div
      className="relative pl-8 pb-4 last:pb-1"
      data-testid={`timeline-event-${event.type}`}
    >
      {/* Connector line (drawn behind dots) */}
      <span className="absolute left-3 top-3 bottom-0 w-px bg-gradient-to-b from-white/15 via-white/5 to-transparent" aria-hidden />
      {/* Event dot */}
      <span className={`absolute left-[6px] top-1.5 w-3 h-3 rounded-full ${tone.dot}`} aria-hidden />
      <div className={`rounded-lg border ${tone.ring} bg-black/40 p-2.5`}>
        <div className="flex items-start gap-2">
          <span className="text-base leading-none mt-0.5">{icon}</span>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <p className={`font-bold text-sm ${tone.text}`}>{event.title}</p>
              {event.is_manual && (
                <span className="text-[9px] uppercase tracking-wider font-black px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-400/40 text-amber-200">
                  Manual
                </span>
              )}
              {event.is_warning && (
                <span className="text-[9px] uppercase tracking-wider font-black px-1.5 py-0.5 rounded bg-rose-500/15 border border-rose-400/40 text-rose-200">
                  Warning
                </span>
              )}
              {typeof event.progress_pct === "number" && (
                <span className="text-[10px] text-amber-300/80 font-bold ml-auto">
                  {event.progress_pct}%
                </span>
              )}
            </div>
            {event.subtitle && (
              <p className="text-[11px] text-gray-300/90 mt-1 leading-snug break-words">
                {event.subtitle}
              </p>
            )}
            {(event.actor_email || event.actor_user_id || event.actor_note) && (
              <div className="mt-1.5 flex flex-wrap gap-x-2 gap-y-0.5 text-[10px] text-gray-400">
                {event.actor_email && (
                  <span>
                    by <span className="text-amber-200 font-semibold">{event.actor_email}</span>
                  </span>
                )}
                {!event.actor_email && event.actor_user_id && (
                  <span>by <span className="text-amber-200 font-mono">{event.actor_user_id}</span></span>
                )}
                {event.actor_note && (
                  <span className="italic text-gray-400/90">— {event.actor_note}</span>
                )}
              </div>
            )}
            <div className="mt-1 flex items-center gap-2 text-[10px] text-gray-500 font-mono">
              <span>{fmtTime(event.at)}</span>
              <span className="text-gray-700">·</span>
              <span>{relTime(event.at)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const CampaignTimelineModal = ({ submissionId, open, onOpenChange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    if (!submissionId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/admin/promo/timeline/${encodeURIComponent(submissionId)}`);
      setData(r.data?.timeline || null);
    } catch (e) {
      setError(e?.response?.data?.detail?.reason || e?.response?.data?.detail || e?.message || "load_failed");
    } finally {
      setLoading(false);
    }
  }, [submissionId]);

  useEffect(() => {
    if (open) {
      setFilter("all");
      setQuery("");
      load();
    }
  }, [open, load]);

  const s = data?.summary || {};
  const matchesQuery = (e) => {
    const q = (query || "").toLowerCase().trim();
    if (!q) return true;
    const haystack = `${e.title} ${e.subtitle || ""} ${e.actor_email || ""} ${e.actor_user_id || ""} ${e.actor_note || ""}`.toLowerCase();
    return haystack.includes(q);
  };
  const events = (data?.events || []).filter(
    (e) => (FILTERS.find((f) => f.key === filter) || FILTERS[0]).match(e) && matchesQuery(e),
  );
  const visibleCount = events.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="campaign-timeline-modal"
        className="bg-gradient-to-br from-[#160b22] via-[#0d0717] to-[#0a0514] border-amber-400/30 text-white max-w-2xl max-h-[88vh] overflow-hidden flex flex-col p-0"
      >
        <DialogHeader className="px-5 pt-4 pb-3 border-b border-amber-500/20 shrink-0">
          <DialogTitle className="text-lg flex items-center gap-2">
            <span>🕒</span>
            <span>Campaign Timeline</span>
          </DialogTitle>
          {data && (
            <>
              <p className="text-sm text-amber-100 font-bold mt-1 truncate" data-testid="timeline-track-title">
                {data.track_title}
              </p>
              <p className="text-xs text-gray-400 truncate">
                <span className="text-amber-300/80">{data.artist_name}</span>
                <span className="text-gray-600 mx-1.5">·</span>
                <span className="font-mono text-[10px]">{data.submission_id}</span>
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
                <SummaryPill label="Tier" tone="amber" value={(s.tier || "—").toUpperCase()} testid="timeline-sum-tier" />
                <SummaryPill label="Progress" tone="emerald"
                  value={`${(s.promotional_plays || 0).toLocaleString()}/${(s.promo_target || 0).toLocaleString()} · ${s.progress_pct || 0}%`}
                  testid="timeline-sum-progress" />
                <SummaryPill label={s.feature_active ? "Status" : "Frozen"} tone={s.feature_active ? "emerald" : "purple"}
                  value={s.feature_active ? "ACTIVE" : "FROZEN"} testid="timeline-sum-status" />
                <SummaryPill label="Mode" tone="purple" value={(s.promo_mode || "fixed").toUpperCase()} testid="timeline-sum-mode" />
              </div>
            </>
          )}
        </DialogHeader>

        {/* Filter + search */}
        <div className="px-5 py-2.5 border-b border-amber-500/15 shrink-0 flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="flex flex-wrap gap-1.5" data-testid="timeline-filters">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`timeline-filter-${f.key}`}
                className={`text-[10px] uppercase tracking-wider font-black px-2 py-1 rounded-full border transition-all ${
                  filter === f.key
                    ? "bg-amber-500/25 border-amber-300/70 text-amber-100"
                    : "bg-black/30 border-amber-500/15 text-gray-400 hover:text-amber-200 hover:border-amber-400/40"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search events / actor / note…"
            data-testid="timeline-search"
            className="sm:ml-auto bg-black/40 border border-amber-500/20 rounded-md px-2.5 py-1 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-amber-400/50 w-full sm:w-56"
          />
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading && (
            <p className="text-sm text-gray-400 text-center py-8" data-testid="timeline-loading">
              Loading campaign timeline…
            </p>
          )}
          {error && !loading && (
            <p className="text-sm text-rose-300 text-center py-8" data-testid="timeline-error">
              Could not load timeline: {String(error)}
            </p>
          )}
          {!loading && !error && data && events.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-8" data-testid="timeline-empty">
              {(query || filter !== "all")
                ? "No events match your filter / search."
                : "No events recorded for this campaign yet."}
            </p>
          )}
          {!loading && !error && events.length > 0 && (
            <div data-testid="timeline-events-list">
              {events.map((e, i) => (
                <TimelineEvent key={`${e.at}-${e.type}-${i}`} event={e} />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-2.5 border-t border-amber-500/15 shrink-0 flex items-center gap-3 text-[10px] text-gray-500">
          <span data-testid="timeline-count">{visibleCount} event{visibleCount === 1 ? "" : "s"}</span>
          <span className="text-gray-700">·</span>
          <span>Sorted newest first · All times in your local TZ</span>
          <Button
            onClick={load}
            variant="ghost"
            size="sm"
            disabled={loading}
            className="ml-auto text-amber-200 hover:text-white hover:bg-amber-500/15 h-7 px-2"
            data-testid="timeline-refresh"
          >
            <span className="text-[11px]">{loading ? "Refreshing" : "Refresh"}</span>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CampaignTimelineModal;
